import { useCallback, useContext, useMemo, useState } from 'react'
import { useToast } from 'ui'
import { MESSAGE_TEMPLATES_QUERY_KEY } from '../components/MessageTemplates'
import {
  DeliveryChannel,
  IMessageTemplate,
  MessageTemplateCtx,
} from '../providers/MessageTemplate.provider'
import { IMessageTemplateSchema } from '../schemas/message-template.schema'
import { useError } from './useError'
import { useQueryClientSingleton } from './useQueryClientSingleton'

export const useMessageTemplate = (props: {
  messageTemplates: IMessageTemplate[]
}) => {
  const findMessageTemplates = useCallback(
    (channel: IMessageTemplate['channel']) =>
      props.messageTemplates?.filter(
        (messageTemplate) => messageTemplate.channel === channel,
      ),
    [props.messageTemplates],
  )

  const [createMessageTemplate, setCreateMessageTemplate] =
    useState<DeliveryChannel>()

  const formId = 'create-message-template-form'

  const [isLoading, setIsLoading] = useState(false)

  const toast = useToast()

  const error = useError()

  const queryClient = useQueryClientSingleton()

  const { postMethod, channels, multiple } = useContext(MessageTemplateCtx)

  const onCreateMessageTemplate = useCallback(
    (messageTemplate: IMessageTemplateSchema, onSuccess: () => void) => {
      if (!createMessageTemplate) return

      setIsLoading(true)

      postMethod({
        template: messageTemplate,
        channel: createMessageTemplate,
      })
        .then((res) => {
          queryClient.invalidateQueries([MESSAGE_TEMPLATES_QUERY_KEY])

          onSuccess()

          setCreateMessageTemplate(undefined)
        })
        .catch(error.handleError)
        .finally(() => setIsLoading(false))
    },
    [createMessageTemplate],
  )

  const [tabIndex, setTabIndex] = useState(0)

  const currentMessageTemplates = useMemo(() => {
    const channel = channels[tabIndex]
    return findMessageTemplates(channel)
  }, [tabIndex, props.messageTemplates])

  const currentChannel = useMemo(() => channels[tabIndex], [tabIndex])

  return {
    currentChannel,
    currentMessageTemplates,
    setTabIndex,
    onCreateMessageTemplate,
    tabIndex,
    isLoading,
    channels,
    multiple,
    createMessageTemplate,
    setCreateMessageTemplate,
    formId,
  }
}
